import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';

interface CurrentRun {
  status: 'running' | 'completed' | 'failed';
  total?: number;
  processed?: number;
  contacts_synced?: number;
  contacts_created?: number;
  contacts_updated?: number;
  contacts_skipped?: number;
  opportunities_synced?: number;
  errors?: string[];
  message?: string;
}

interface SyncStatus {
  configured: boolean;
  ghl_contacts: number;
  ghl_opportunities: number;
  pending_conflicts: number;
  last_sync_at: string | null;
  current_run?: CurrentRun | null;
}

interface Conflict {
  id: string;
  entity_type: string;
  field_name: string;
  ghl_value: string;
  apex_value: string;
  resolution: string;
  created_at: string;
}

interface CrossRef {
  id: string;
  entity_type: string;
  ghl_id: string;
  apex_id: string;
  match_method: string;
  match_confidence: number;
  created_at: string;
}

function timeAgo(date: string | null): string {
  if (!date) return 'Never';
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function GHLSync() {
  const [tab, setTab] = useState<'status' | 'conflicts' | 'crossref'>('status');
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [crossrefs, setCrossrefs] = useState<CrossRef[]>([]);
  const [crossrefTotal, setCrossrefTotal] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.getGHLSyncStatus();
      setStatus(res);
    } catch { /* ignore */ }
  }, []);

  const fetchConflicts = useCallback(async () => {
    try {
      const res = await api.listGHLConflicts();
      setConflicts(res.conflicts || []);
    } catch { /* ignore */ }
  }, []);

  const fetchCrossrefs = useCallback(async () => {
    try {
      const res = await api.listGHLCrossrefs();
      setCrossrefs(res.crossrefs || []);
      setCrossrefTotal(res.total || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchStatus(), fetchConflicts(), fetchCrossrefs()]).finally(() =>
      setLoading(false)
    );
  }, [fetchStatus, fetchConflicts, fetchCrossrefs]);

  const triggerSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      // Starts a background sync and returns immediately (202). We then poll
      // /status until the run reports completed/failed — a full sync of
      // thousands of contacts runs server-side and would time out a single request.
      await api.triggerGHLSync();

      let done = false;
      while (!done) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await api.getGHLSyncStatus();
        setStatus(s);
        const run: CurrentRun | undefined | null = s.current_run;
        if (!run || run.status === 'completed' || run.status === 'failed') {
          done = true;
          if (run?.status === 'failed') {
            setSyncResult({ error: run.message || 'Sync failed' });
          } else if (run?.status === 'completed') {
            setSyncResult(run);
          }
        }
      }
      await fetchConflicts();
      await fetchCrossrefs();
    } catch (err: any) {
      setSyncResult({ error: err?.message || 'Failed to start sync' });
    } finally {
      setSyncing(false);
    }
  };

  const resolveConflict = async (id: string, resolution: string) => {
    try {
      await api.resolveGHLConflict(id, resolution);
      await fetchConflicts();
      await fetchStatus();
    } catch { /* ignore */ }
  };

  const tabs = [
    { key: 'status' as const, label: 'Dashboard' },
    { key: 'conflicts' as const, label: `Conflicts (${status?.pending_conflicts || 0})` },
    { key: 'crossref' as const, label: `Cross-Refs (${crossrefTotal})` },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">GHL Sync</h1>
            <p className="text-sm text-gray-500 mt-1">GoHighLevel CRM integration & sync management</p>
          </div>
          <button
            onClick={triggerSync}
            disabled={syncing || !status?.configured}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 text-sm font-medium"
          >
            {syncing ? 'Syncing...' : 'Trigger Sync'}
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`pb-3 text-sm font-medium border-b-2 ${
                  tab === t.key
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : tab === 'status' ? (
          <div className="space-y-6">
            {/* Config check */}
            {!status?.configured && (
              <div className="bg-warning-wash border border-warning/30 rounded-lg p-4">
                <p className="text-sm text-warning font-medium">GHL Not Configured</p>
                <p className="text-xs text-warning mt-1">
                  Set GHL_API_KEY and GHL_LOCATION_ID environment variables on Railway.
                </p>
              </div>
            )}

            {/* Live progress while a background sync runs */}
            {syncing && (
              <div className="bg-info-wash border border-info/30 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-info">
                    Syncing contacts… {status?.current_run?.processed ?? 0}
                    {status?.current_run?.total ? ` / ${status.current_run.total}` : ''}
                  </p>
                  <span className="text-xs text-info">runs in the background — safe to leave open</span>
                </div>
                {!!status?.current_run?.total && (
                  <div className="mt-2 h-2 w-full rounded-full bg-info-wash overflow-hidden">
                    <div
                      className="h-full bg-primary-500 transition-all"
                      style={{
                        width: `${Math.min(
                          100,
                          Math.round(((status.current_run.processed ?? 0) / status.current_run.total) * 100)
                        )}%`,
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Stats cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500 uppercase">Contacts Synced</p>
                <p className="text-2xl font-bold text-gray-900">{status?.ghl_contacts || 0}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500 uppercase">Opportunities</p>
                <p className="text-2xl font-bold text-gray-900">{status?.ghl_opportunities || 0}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500 uppercase">Pending Conflicts</p>
                <p className="text-2xl font-bold text-warning">{status?.pending_conflicts || 0}</p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-xs text-gray-500 uppercase">Last Sync</p>
                <p className="text-lg font-semibold text-gray-900">{timeAgo(status?.last_sync_at || null)}</p>
              </div>
            </div>

            {/* Sync result */}
            {syncResult && (
              <div className={`rounded-lg border p-4 ${syncResult.error ? 'bg-red-50 border-red-200' : 'bg-success-wash border-success/30'}`}>
                {syncResult.error ? (
                  <p className="text-sm text-red-700">{syncResult.error}</p>
                ) : (
                  <div className="space-y-1 text-sm text-success">
                    <p className="font-medium">Sync Complete</p>
                    <p>Contacts synced: {syncResult.contacts_synced}, Created: {syncResult.contacts_created}, Updated: {syncResult.contacts_updated}</p>
                    <p>Opportunities synced: {syncResult.opportunities_synced}, Created: {syncResult.opportunities_created}</p>
                    {syncResult.conflicts_found > 0 && (
                      <p className="text-warning">Conflicts found: {syncResult.conflicts_found}</p>
                    )}
                    {syncResult.errors?.length > 0 && (
                      <p className="text-red-700">Errors: {syncResult.errors.length}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : tab === 'conflicts' ? (
          <div className="space-y-4">
            {conflicts.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No pending conflicts</div>
            ) : (
              <div className="bg-white rounded-lg border overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Field</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">GHL Value</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Apex Value</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {conflicts.map((c) => (
                      <tr key={c.id}>
                        <td className="px-4 py-3 text-sm text-gray-900">{c.entity_type}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{c.field_name}</td>
                        <td className="px-4 py-3 text-sm text-info font-mono">{c.ghl_value || '—'}</td>
                        <td className="px-4 py-3 text-sm text-success font-mono">{c.apex_value || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => resolveConflict(c.id, 'use_ghl')}
                              className="text-xs px-2 py-1 bg-info-wash text-info rounded hover:bg-info-wash"
                            >
                              Use GHL
                            </button>
                            <button
                              onClick={() => resolveConflict(c.id, 'use_apex')}
                              className="text-xs px-2 py-1 bg-success-wash text-success rounded hover:bg-success-wash"
                            >
                              Use Apex
                            </button>
                            <button
                              onClick={() => resolveConflict(c.id, 'dismissed')}
                              className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                            >
                              Dismiss
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {crossrefs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No cross-references yet. Run a sync first.</div>
            ) : (
              <div className="bg-white rounded-lg border overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">GHL ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Apex ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Match Method</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confidence</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {crossrefs.map((cr) => (
                      <tr key={cr.id}>
                        <td className="px-4 py-3 text-sm">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {cr.entity_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-600">{cr.ghl_id.slice(0, 12)}...</td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-600">{cr.apex_id.slice(0, 12)}...</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{cr.match_method}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`font-medium ${cr.match_confidence >= 0.9 ? 'text-success' : cr.match_confidence >= 0.7 ? 'text-warning' : 'text-red-600'}`}>
                            {(cr.match_confidence * 100).toFixed(0)}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{new Date(cr.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
